import { createContext, type RefObject } from 'react';
import type { CycleStateCode, Movement, PeriodResult, RegimeCode, YearMonth } from '../model/period';
import { creditNoteTracking } from './creditNotes';

/** Contexto común de los eventos `declarations_*`: cada pantalla aporta lo suyo con `TrackingScope`. */
export type TrackingProps = Record<string, string | number | boolean | undefined>;

export const DeclarationsTrackingContext = createContext<TrackingProps>({});

export const TrackingMutedContext = createContext(false);

export type ScreenVisit = { shownAt: number; sections: Set<string> };

export const ScreenVisitContext = createContext<RefObject<ScreenVisit | null> | null>(null);

export const newScreenVisit = (): ScreenVisit => ({ shownAt: Date.now(), sections: new Set() });

export const sectionsSeenOf = (visit?: ScreenVisit | null) =>
	visit ? { sections_seen: [...visit.sections].join(',') || 'ninguna', sections_seen_count: visit.sections.size } : {};

export type TrackingSurface =
	| 'home_widget'
	| 'year'
	| 'period'
	| 'annual'
	| 'payroll_month'
	| 'payroll_receipt'
	| 'invoices'
	| 'invoice'
	| 'catch_up'
	| 'por_aprobar';

const REGIME_NAMES: Record<RegimeCode, string> = {
	'626': 'resico',
	'625': 'plataformas',
	'612': 'actividad_empresarial',
};

export type OpenedFrom = 'focus_card' | 'month_tile' | 'list_row' | 'calendar' | 'activity' | 'payroll_bar';

export type CatchUpFrom = 'health_button' | 'unfiled_group';

export const regimeTracking = (regime?: RegimeCode | null) => (regime ? REGIME_NAMES[regime] : undefined);

export const periodTracking = (period?: string | null) => (period ? period.replace('-', '_') : undefined);

export const amountBand = (amount?: number | null) => {
	if (amount === undefined || amount === null) return undefined;
	const value = Math.abs(amount);
	if (value < 1) return '0';
	if (value < 500) return '1_500';
	if (value < 2000) return '500_2k';
	if (value < 10000) return '2k_10k';
	return '10k_mas';
};

export const cellTracking = (cell: {
	period?: string;
	regime?: RegimeCode | null;
	state?: CycleStateCode | null;
	result?: PeriodResult | null;
	total?: number | null;
}) => ({
	period: periodTracking(cell.period),
	regime: regimeTracking(cell.regime),
	cycle_state: cell.state ?? undefined,
	result: cell.result ?? undefined,
	amount_band: amountBand(cell.total),
});

export const monthTracking = (months: YearMonth[], period: string, regime?: RegimeCode) => {
	const month = months.find((candidate) => candidate.period === period);
	const cell = month?.cells?.find((candidate) => candidate.regime === (regime ?? month.regime)) ?? month;
	return cellTracking({ period, regime: regime ?? month?.regime, state: cell?.state, result: cell?.result, total: cell?.total });
};

export const scoreBand = (score?: number | null) => {
	if (score === undefined || score === null) return undefined;
	if (score < 0.5) return '0_49';
	if (score < 0.8) return '50_79';
	if (score < 1) return '80_99';
	return '100';
};

export const movementTracking = (movement: Pick<Movement, 'kind' | 'category' | 'creditNote'>) => ({
	kind: movement.kind,
	category: movement.category,
	...creditNoteTracking(movement),
});

export const definedProps = (props: TrackingProps): TrackingProps =>
	Object.fromEntries(Object.entries(props).filter(([, value]) => value !== undefined));

let previousSurface: string | undefined;

export const takePreviousSurface = (surface: string) => {
	const from = previousSurface ?? 'none';
	previousSurface = surface;
	return from;
};

const FLOW_IDLE_MS = 30 * 60 * 1000;

let flow: { id: string; startedAt: number; lastAt: number } | undefined;

const newFlowId = (now: number) => globalThis.crypto?.randomUUID?.() ?? `${now}-${Math.random().toString(36).slice(2)}`;

export const flowTracking = (now: number) => {
	if (!flow || now - flow.lastAt > FLOW_IDLE_MS) flow = { id: newFlowId(now), startedAt: now, lastAt: now };
	flow.lastAt = now;
	return { flow_id: flow.id, seconds_in_flow: Math.round((now - flow.startedAt) / 100) / 10 };
};
